import React from 'react';
import { call, put, select } from 'redux-saga/effects';
import { TestStepStates, buildMapStateToProps, sleep, exceptionToObject } from './core';
import { testStatusUpdate } from '../store/actions';
import { connect } from 'react-redux';
import ResultRow from '../components/automatedTests/ResultRow';
import { useTranslation } from 'react-i18next';
import { VideoConnectionFailed, VideoError, VideoFailed, VideoWarning } from '../components/automatedTests/resultsSummaries';
import PropTypes from 'prop-types';

import { DefaultMeetingSession, MeetingSessionConfiguration, MeetingSessionStatusCode, ConsoleLogger, LogLevel, DefaultDeviceController } from 'amazon-chime-sdk-js';

export const TEST_ID = 'ChimeConnectivity';

/**
 * Connectivity test for Chime. Joins a meeting as two attendees - one sending the user's microphone and camera, one
 * only receiving - then samples the media stats of both over a measurement window.
 * @module testModules:chimeVideo
 */

/** How long to wait for both attendees to connect before treating the video service as unreachable. */
const CONNECT_TIMEOUT_MS = 20000;
/** Time allowed after connecting for the bandwidth estimate and video bitrate to ramp up before sampling. */
const WARM_UP_MS = 4000;
/** How long to sample stats for. The median is judged, so a single good or bad sample can't decide the result. */
const MEASUREMENT_MS = 12000;
const POLL_INTERVAL_MS = 500;

/**
 * Pass thresholds. Bandwidth is in bits per second, latency in milliseconds and packet loss in percent.
 * Bandwidth is only judged when a camera is available, as an audio-only call doesn't build a meaningful estimate.
 */
export const Thresholds = {
    failBandwidth: 150000,
    warnBandwidth: 300000,
    failLatency: 1000,
    warnLatency: 400,
    failPacketLoss: 20,
    warnPacketLoss: 5
};

const getVideoStats = (data, metric) => {
    if (!data || typeof data !== 'object') return null;

    for (const outerValue of Object.values(data)) {
        if (outerValue && typeof outerValue === 'object') {
            for (const innerValue of Object.values(outerValue)) {
                if (innerValue && metric in innerValue) {
                    return innerValue[metric]; // Return first found metric value
                }
            }
        }
    }

    return null;
};

/**
 * Observes a single attendee's meeting session, recording whether it connected, why it stopped (if it did), and a
 * timestamped sample of its stats each time the SDK reports them.
 */
class SessionObserver {
    constructor() {
        this.started = false;
        this.stopReason = null;
        this.samples = [];
    }

    audioVideoDidStart() {
        this.started = true;
    }

    audioVideoDidStop(sessionStatus) {
        this.stopReason = MeetingSessionStatusCode[sessionStatus.statusCode()] ?? 'Unknown';
    }

    metricsDidReceive(clientMetricReport) {
        const videoMetrics = clientMetricReport.getObservableVideoMetrics();
        this.samples.push({
            ...clientMetricReport.getObservableMetrics(),
            time: Date.now(),
            videoUpstreamBitrate: getVideoStats(videoMetrics, 'videoUpstreamBitrate'),
            videoUpstreamRoundTripTimeMs: getVideoStats(videoMetrics, 'videoUpstreamRoundTripTimeMs'),
            videoUpstreamJitterMs: getVideoStats(videoMetrics, 'videoUpstreamJitterMs'),
            videoDownstreamBitrate: getVideoStats(videoMetrics, 'videoDownstreamBitrate'),
            videoDownstreamDelayMs: getVideoStats(videoMetrics, 'videoDownstreamDelayMs')
        });
    }
}

const selectMeetingInfo = (store) => {
    return {
        meeting: store.configReducer.chime.meeting,
        sendAttendee: store.configReducer.chime.attendeeSend,
        recvAttendee: store.configReducer.chime.attendeeRecv
    };
};

const startDevice = async (listDevices, start, role) => {
    try {
        const devices = await listDevices();
        if (devices.length === 0) {
            console.error(`[${role}] No devices found`);
            return null;
        }
        return await start(devices[0].deviceId);
    } catch (e) {
        // Device problems are reported by the capture tests - carry on and test the connection without it.
        console.error(`[${role}] Failed to start device`, e);
        return null;
    }
};

const connectToChime = async ({ meeting, attendee, observer, sendMedia, role }) => {
    const logger = new ConsoleLogger('ChimeLogger', LogLevel.ERROR);
    const configuration = new MeetingSessionConfiguration(meeting, attendee);
    const deviceController = new DefaultDeviceController(logger);
    const meetingSession = new DefaultMeetingSession(configuration, logger, deviceController);

    const audioVideo = meetingSession.audioVideo;
    audioVideo.addObserver(observer);

    let hasCamera = false;
    if (sendMedia) {
        // Send the real microphone, so audio is tested end to end rather than the SDK's silent placeholder.
        await startDevice(() => audioVideo.listAudioInputDevices(), (id) => audioVideo.startAudioInput(id), role);

        const videoStream = await startDevice(() => audioVideo.listVideoInputDevices(), (id) => audioVideo.startVideoInput(id), role);
        hasCamera = !!videoStream && videoStream.getVideoTracks().length > 0;
    }

    audioVideo.start();

    if (hasCamera) {
        audioVideo.startLocalVideoTile();
    }

    return { meetingSession, hasCamera };
};

const stopSession = (meetingSession) => {
    const audioVideo = meetingSession.audioVideo;
    audioVideo.stop();
    // Release the camera and microphone for the recording test.
    Promise.all([audioVideo.stopVideoInput(), audioVideo.stopAudioInput()]).catch((e) => console.error(e));
};

/**
 * Median of the numeric values in a list, or null if there are none.
 * @param {Array} values
 * @param {boolean} ignoreZero Treat zero as missing, for metrics where zero means "not reported".
 */
export const median = (values, ignoreZero = false) => {
    const sorted = values
        .filter(v => typeof v === 'number' && !isNaN(v) && (!ignoreZero || v > 0))
        .sort((a, b) => a - b);

    if (sorted.length === 0) return null;

    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

const medianOf = (samples, metric, ignoreZero = false) => median(samples.map(s => s[metric]), ignoreZero);

const firstAvailable = (...values) => values.find(v => v !== null && v !== undefined) ?? null;

const round = (value) => value === null ? null : Math.round(value);

/**
 * Judges the stats sampled from the sending and receiving attendees.
 * @param {Object} params
 * @param {Object[]} params.sendSamples Samples from the attendee sending the microphone and camera.
 * @param {Object[]} params.recvSamples Samples from the receive-only attendee.
 * @param {boolean} params.hasCamera Whether video was sent - if not, bandwidth isn't judged.
 * @returns {{status: string, values: Object}} The test status, and values to submit with the results.
 */
export const evaluateSamples = ({ sendSamples, recvSamples, hasCamera }) => {
    // The bandwidth estimate reflects what the connection can carry. The video bitrate depends on the camera
    // picture too, so it's only used when there's no estimate.
    const sendBandwidth = firstAvailable(
        medianOf(sendSamples, 'availableOutgoingBitrate', true),
        medianOf(sendSamples, 'videoUpstreamBitrate'));
    const recvBandwidth = medianOf(recvSamples, 'videoDownstreamBitrate');
    const latency = firstAvailable(
        medianOf(sendSamples, 'currentRoundTripTimeMs', true),
        medianOf(sendSamples, 'audioUpstreamRoundTripTimeMs', true),
        medianOf(sendSamples, 'videoUpstreamRoundTripTimeMs', true));
    const audioPacketsSent = medianOf(sendSamples, 'audioPacketsSent');
    const audioPacketsReceived = medianOf(recvSamples, 'audioPacketsReceived');
    const audioPacketLoss = medianOf(recvSamples, 'audioPacketsReceivedFractionLoss');

    const values = {
        exception: null,
        connectionFailed: false,
        hasCamera,
        stats_sendBandwidth: round(sendBandwidth) ?? 0,
        stats_recvBandwidth: round(recvBandwidth) ?? 0,
        stats_sendLatency: round(latency) ?? 0,
        stats_sendJitter: round(firstAvailable(medianOf(sendSamples, 'videoUpstreamJitterMs'), medianOf(sendSamples, 'audioUpstreamJitterMs'))),
        stats_recvLatency: round(medianOf(recvSamples, 'videoDownstreamDelayMs')),
        stats_audioPacketsSent: round(audioPacketsSent) ?? 0,
        stats_audioPacketsReceived: round(audioPacketsReceived) ?? 0,
        stats_audioPacketLossPercent: round(audioPacketLoss) ?? 0
    };

    // Both attendees connected, so the media path isn't blocked - audio that doesn't get through (or only some of the
    // time) means the connection is too poor to carry it.
    const audioFailed = !audioPacketsSent || !audioPacketsReceived;

    const isBelow = (threshold) => hasCamera && (values.stats_sendBandwidth < threshold || values.stats_recvBandwidth < threshold);

    if (audioFailed || isBelow(Thresholds.failBandwidth) || latency > Thresholds.failLatency || audioPacketLoss > Thresholds.failPacketLoss) {
        return { status: TestStepStates.Failed, values };
    }

    if (isBelow(Thresholds.warnBandwidth) || latency > Thresholds.warnLatency || audioPacketLoss > Thresholds.warnPacketLoss) {
        return { status: TestStepStates.Warning, values };
    }

    return { status: TestStepStates.Passed, values };
};

export function *runTest () {
    yield put (testStatusUpdate(TEST_ID, TestStepStates.Running, null));
    const { meeting, sendAttendee, recvAttendee } = yield select(selectMeetingInfo);
    let status = TestStepStates.Failed;
    let result = {
        exception: null,
        state: 'not done'
    };

    const sendObserver = new SessionObserver();
    const recvObserver = new SessionObserver();
    const sessions = [];

    const connected = () => sendObserver.started && recvObserver.started;
    const stopReason = () => sendObserver.stopReason ?? recvObserver.stopReason;
    const connectionFailed = (exception) => {
        status = TestStepStates.Error;
        result = { ...result, exception, connectionFailed: true, state: 'completed' };
    };

    try {
        const send = yield call(connectToChime, { meeting, attendee: sendAttendee, observer: sendObserver, sendMedia: true, role: 'send' });
        sessions.push(send.meetingSession);
        const recv = yield call(connectToChime, { meeting, attendee: recvAttendee, observer: recvObserver, sendMedia: false, role: 'recv' });
        sessions.push(recv.meetingSession);

        const connectIterations = CONNECT_TIMEOUT_MS / POLL_INTERVAL_MS;
        for (let i = 0; i < connectIterations && !connected() && !stopReason(); i++) {
            yield put (testStatusUpdate(TEST_ID, TestStepStates.Running, null, (30 * i) / connectIterations));
            yield call(sleep, POLL_INTERVAL_MS);
        }

        if (stopReason()) {
            connectionFailed(`Failed to connect - ${stopReason()}.`);
        } else if (!connected()) {
            connectionFailed('Failed to execute test - timed out.');
        } else {
            yield put (testStatusUpdate(TEST_ID, TestStepStates.Running, null, 30));
            yield call(sleep, WARM_UP_MS);

            const measureStart = Date.now();
            const measureIterations = MEASUREMENT_MS / POLL_INTERVAL_MS;
            for (let i = 0; i < measureIterations && !stopReason(); i++) {
                yield put (testStatusUpdate(TEST_ID, TestStepStates.Running, null, 35 + (60 * i) / measureIterations));
                yield call(sleep, POLL_INTERVAL_MS);
            }

            if (stopReason()) {
                connectionFailed(`Connection dropped - ${stopReason()}.`);
            } else {
                const evaluation = evaluateSamples({
                    sendSamples: sendObserver.samples.filter(s => s.time >= measureStart),
                    recvSamples: recvObserver.samples.filter(s => s.time >= measureStart),
                    hasCamera: send.hasCamera
                });
                status = evaluation.status;
                result = { ...evaluation.values, state: 'completed' };
                console.info(`[${TEST_ID}] ${status}`, result);
            }
        }
    } catch (e) {
        result.exception = exceptionToObject(e).name;
        console.error(result, e);
        status = TestStepStates.Error;
        yield call(sleep, 600); // For looks
    } finally {
        sessions.forEach(stopSession);
    }

    yield put (testStatusUpdate(TEST_ID, TestStepStates.Running, null, 95));
    yield put(testStatusUpdate(TEST_ID, status, result));
}

const ChimeVideoResultRow = ({status, result, progressPercentage}) => {
    const { t } = useTranslation();
    let error = '';

    switch (status) {
        case TestStepStates.Error:
            error = result?.connectionFailed ? <VideoConnectionFailed /> : <VideoError />;
            break;
        case TestStepStates.Failed:
            error = <VideoFailed />;
            break;
        case TestStepStates.Warning:
            error = <VideoWarning />;
            break;
        default:
            break;
    }

    return <ResultRow
        status={status}
        title={t('automaticTests.results.twilioVideo.title')}
        subtitle={t('automaticTests.results.twilioVideo.subtitle')}
        errorMessage={error}
        progressPercentage={progressPercentage}
    />;
};

ChimeVideoResultRow.propTypes = {
    status: PropTypes.string,
    result: PropTypes.object,
    progressPercentage: PropTypes.number
};

const mapStateToProps = buildMapStateToProps(TEST_ID);

export const ChimeVideoResultRowContainer = connect(mapStateToProps)(ChimeVideoResultRow);
